import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useGameStore } from "../store/useGameStore";
import {
  adminApi,
  type AdminConsumableGrantOption,
  type AdminGemGrantOption,
  type AdminItemTemplateOption,
  type AdminMaterialGrantOption,
  type AdminPlayerOption,
} from "../api/admin";
import {
  AdminTokenSearchInput,
  adminSearchQueryFromTokens,
} from "../components/AdminTokenSearchInput";

type Target = { kind: "self" } | { kind: "player"; player: AdminPlayerOption };

export function AdminGrantsScreen() {
  const { setScreen, user, hero, balance, refreshBalance, refreshHero, pushInfoMessages } = useGameStore();
  const [target, setTarget] = useState<Target>({ kind: "self" });
  const [playerQuery, setPlayerQuery] = useState("");
  const [playerResults, setPlayerResults] = useState<AdminPlayerOption[]>([]);
  const [itemTokens, setItemTokens] = useState<string[]>([]);
  const [itemDraft, setItemDraft] = useState("");
  const itemSearchQuery = useMemo(
    () => adminSearchQueryFromTokens(itemTokens, itemDraft),
    [itemTokens, itemDraft],
  );
  const [itemResults, setItemResults] = useState<AdminItemTemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AdminItemTemplateOption | null>(null);
  const [enhanceLevel, setEnhanceLevel] = useState("0");
  const [goldAmount, setGoldAmount] = useState("1000000");
  const [diamondAmount, setDiamondAmount] = useState("100");
  const [starDustAmount, setStarDustAmount] = useState("500");
  const [consumables, setConsumables] = useState<AdminConsumableGrantOption[]>([]);
  const [selectedConsumable, setSelectedConsumable] = useState<AdminConsumableGrantOption | null>(
    null,
  );
  const [consumableQty, setConsumableQty] = useState("1");
  const [gemOptions, setGemOptions] = useState<AdminGemGrantOption[]>([]);
  const [gemQuery, setGemQuery] = useState("");
  const [selectedGem, setSelectedGem] = useState<AdminGemGrantOption | null>(null);
  const [gemQty, setGemQty] = useState("1");
  const [craftMaterials, setCraftMaterials] = useState<AdminMaterialGrantOption[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<AdminMaterialGrantOption | null>(null);
  const [materialQty, setMaterialQty] = useState("100");
  const [revokeVip, setRevokeVip] = useState(true);
  const [revokePro, setRevokePro] = useState(true);
  const [loading, setLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetUserId = target.kind === "self" ? null : target.player.user_id;

  const loadItemTemplates = useCallback(async (q: string) => {
    try {
      const res = await adminApi.searchItemTemplates(q);
      setItemResults(res.templates);
    } catch {
      setItemResults([]);
    }
  }, []);

  useEffect(() => {
    void loadItemTemplates("");
  }, [loadItemTemplates]);

  useEffect(() => {
    void adminApi.listGrantableConsumables().then(setConsumables).catch(() => setConsumables([]));
    void adminApi.listGrantableGems().then(setGemOptions).catch(() => setGemOptions([]));
    void adminApi
      .listGrantableCraftMaterials()
      .then(setCraftMaterials)
      .catch(() => setCraftMaterials([]));
  }, []);

  const luckStones = useMemo(
    () => gemOptions.filter((g) => g.gem_type === "LUCK"),
    [gemOptions],
  );

  const filteredGems = useMemo(() => {
    const q = gemQuery.trim().toLowerCase();
    if (!q) return gemOptions;
    return gemOptions.filter(
      (g) =>
        g.label.toLowerCase().includes(q) ||
        g.gem_type.toLowerCase().includes(q) ||
        String(g.level).includes(q),
    );
  }, [gemOptions, gemQuery]);

  useEffect(() => {
    if (filteredGems.length > 0 && !selectedGem) {
      setSelectedGem(filteredGems[0]);
    }
  }, [filteredGems, selectedGem]);

  useEffect(() => {
    if (consumables.length > 0 && !selectedConsumable) {
      setSelectedConsumable(consumables[0]);
    }
  }, [consumables, selectedConsumable]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadItemTemplates(itemSearchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [itemSearchQuery, loadItemTemplates]);

  useEffect(() => {
    const q = playerQuery.trim();
    if (q.length < 1) {
      setPlayerResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await adminApi.searchPlayers(q);
        setPlayerResults(res.players);
      } catch {
        setPlayerResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [playerQuery]);

  const run = async (key: string, fn: () => Promise<{ message: string; balance?: typeof balance }>) => {
    setLoading(key);
    setMessage(null);
    setError(null);
    try {
      const res = await fn();
      if (res.balance) {
        useGameStore.setState({ balance: res.balance });
      } else if (target.kind === "self") {
        await refreshBalance();
      }
      setMessage(res.message);
      pushInfoMessages([res.message]);
    } catch (e) {
      const text = e instanceof Error ? e.message : "Action failed";
      setError(text);
      pushInfoMessages([`Grant failed: ${text}`]);
    } finally {
      setLoading(null);
    }
  };

  const parseAmount = (value: string) => {
    const amount = Number.parseInt(value, 10);
    if (!Number.isFinite(amount) || amount <= 0) throw new Error("Enter a positive number");
    if (amount > 1_000_000_000_000) throw new Error("Maximum grant is 1,000,000,000,000");
    return amount;
  };

  const targetLabel =
    target.kind === "self"
      ? hero
        ? `${hero.name} (you)`
        : `${user?.first_name ?? user?.username ?? "You"}`
      : target.player.label;

  return (
    <div className="p-4 space-y-4 pb-8">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setScreen("admin")}
          className="text-gray-400 hover:text-white border border-kingdom-mid px-3 py-1.5 rounded-lg text-sm"
        >
          ← Back
        </button>
        <h1 className="text-xl font-bold font-game text-gold-400">🎁 Grants</h1>
      </div>

      <p className="text-gray-400 text-sm">
        Spawn gear, gems, consumables, craft materials (crypto, shards, legendary stones), gold, diamonds, or star dust for any player. Defaults to your account.
      </p>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Recipient</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setTarget({ kind: "self" });
              setPlayerQuery("");
              setPlayerResults([]);
            }}
            className={clsx(
              "text-xs px-3 py-1.5 rounded-full border",
              target.kind === "self"
                ? "border-kingdom-glow text-kingdom-glow bg-kingdom-mid/50"
                : "border-gray-600 text-gray-400",
            )}
          >
            Myself
          </button>
        </div>
        <input
          value={playerQuery}
          onChange={(e) => setPlayerQuery(e.target.value)}
          placeholder="Search by hero name, @username, or user id…"
          className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-sm text-white placeholder:text-gray-500"
        />
        {playerResults.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-kingdom-mid bg-kingdom-dark/80 p-1">
            {playerResults.map((p) => (
              <button
                key={p.user_id}
                type="button"
                onClick={() => {
                  setTarget({ kind: "player", player: p });
                  setPlayerQuery(p.label);
                  setPlayerResults([]);
                }}
                className={clsx(
                  "w-full text-left text-xs px-2 py-2 rounded-md hover:bg-kingdom-mid/60",
                  target.kind === "player" && target.player.user_id === p.user_id
                    ? "bg-kingdom-mid/40 text-kingdom-glow"
                    : "text-gray-300",
                )}
              >
                {p.label}
                {p.is_blocked && <span className="text-rose-400 ml-1">(blocked)</span>}
              </button>
            ))}
          </div>
        )}
        <p className="text-[11px] text-gray-500">
          Target: <span className="text-gray-300">{targetLabel}</span>
        </p>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">VIP / Pro</h2>
        <p className="text-xs text-gray-500">
          Remove active VIP subscription and/or Pro covenant for the recipient above. VIP level EXP is kept; only
          subscription ends.
        </p>
        <div className="flex flex-wrap gap-4 text-sm text-gray-300">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={revokeVip}
              onChange={(e) => setRevokeVip(e.target.checked)}
            />
            Revoke VIP subscription
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={revokePro}
              onChange={(e) => setRevokePro(e.target.checked)}
            />
            Revoke Pro covenant
          </label>
        </div>
        <button
          type="button"
          disabled={loading !== null || (!revokeVip && !revokePro)}
          onClick={() =>
            run("revoke-premium", async () => {
              const res = await adminApi.revokePremium({
                targetUserId,
                revokeVip,
                revokePro,
              });
              if (res.hero) {
                useGameStore.setState({ hero: res.hero });
              } else if (target.kind === "self") {
                await refreshHero();
              }
              return { message: res.message };
            })
          }
          className="w-full py-2.5 rounded-xl border border-rose-500/50 bg-rose-950/30 text-rose-100 text-sm font-bold disabled:opacity-40"
        >
          {loading === "revoke-premium" ? "Removing…" : "Remove selected premium status"}
        </button>
        <p className="text-xs text-gray-500 pt-2 border-t border-kingdom-mid/60">
          Reset weekly VIP chest claim for the recipient (testing). Clears this ISO week&apos;s claim flag so
          &quot;Claim weekly VIP chest&quot; appears again on VIP &amp; Pro. Does not remove chests already in
          inventory.
        </p>
        <button
          type="button"
          disabled={loading !== null}
          onClick={() =>
            run("reset-vip-chest", () => adminApi.resetWeeklyVipChest(targetUserId))
          }
          className="w-full py-2.5 rounded-xl border border-amber-500/40 bg-amber-950/20 text-amber-100 text-sm font-bold disabled:opacity-40"
        >
          {loading === "reset-vip-chest" ? "Resetting…" : "Reset weekly VIP chest claim"}
        </button>
      </section>

      {consumables.length > 0 && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <h2 className="font-bold text-white">Consumables</h2>
          <p className="text-xs text-gray-500">All stash consumables from game config (potions, scrolls, charms).</p>
          <div className="space-y-1">
            {consumables.map((c) => (
              <button
                key={c.item_id}
                type="button"
                onClick={() => setSelectedConsumable(c)}
                className={clsx(
                  "w-full text-left text-xs px-2 py-2 rounded-md border",
                  selectedConsumable?.item_id === c.item_id
                    ? "border-kingdom-glow bg-kingdom-mid/40 text-kingdom-glow"
                    : "border-kingdom-mid text-gray-300 hover:bg-kingdom-mid/30",
                )}
              >
                <span className="font-semibold">{c.label}</span>
                {c.description && <span className="text-gray-500"> · {c.description}</span>}
              </button>
            ))}
          </div>
          <label className="block text-xs text-gray-400">
            Quantity
            <input
              type="number"
              min={1}
              value={consumableQty}
              onChange={(e) => setConsumableQty(e.target.value)}
              className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
            />
          </label>
          <button
            type="button"
            disabled={loading !== null || !selectedConsumable}
            onClick={() =>
              run("consumable", () =>
                adminApi.grantConsumable(
                  selectedConsumable!.item_id,
                  parseAmount(consumableQty),
                  targetUserId,
                ),
              )
            }
            className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
          >
            {loading === "consumable"
              ? "Working…"
              : selectedConsumable
                ? `Grant ${Math.max(1, Number.parseInt(consumableQty, 10) || 1)}× ${selectedConsumable.label}`
                : "Select consumable"}
          </button>
        </section>
      )}

      {gemOptions.length > 0 && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <h2 className="font-bold text-white">Gems</h2>
          <p className="text-xs text-gray-500">
            All gem types and levels. Luck stones boost gem craft and gear enhance success rates.
          </p>
          <input
            value={gemQuery}
            onChange={(e) => setGemQuery(e.target.value)}
            placeholder="Search gems (PATK, LUCK, level…)"
            className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-sm text-white placeholder:text-gray-500"
          />
          <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-kingdom-mid bg-kingdom-dark/80 p-1">
            {filteredGems.map((g) => (
              <button
                key={`${g.gem_type}-${g.level}`}
                type="button"
                onClick={() => setSelectedGem(g)}
                className={clsx(
                  "w-full text-left text-xs px-2 py-2 rounded-md border",
                  selectedGem?.gem_type === g.gem_type && selectedGem?.level === g.level
                    ? "border-kingdom-glow bg-kingdom-mid/40 text-kingdom-glow"
                    : "border-transparent text-gray-300 hover:bg-kingdom-mid/30",
                )}
              >
                <span className="font-semibold">{g.label}</span>
              </button>
            ))}
            {filteredGems.length === 0 && (
              <p className="text-xs text-gray-500 px-2 py-3 text-center">No gems match</p>
            )}
          </div>
          <label className="block text-xs text-gray-400">
            Quantity
            <input
              type="number"
              min={1}
              value={gemQty}
              onChange={(e) => setGemQty(e.target.value)}
              className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
            />
          </label>
          <button
            type="button"
            disabled={loading !== null || !selectedGem}
            onClick={() =>
              run("gem", () =>
                adminApi.grantGem(
                  selectedGem!.gem_type,
                  selectedGem!.level,
                  parseAmount(gemQty),
                  targetUserId,
                ),
              )
            }
            className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
          >
            {loading === "gem"
              ? "Working…"
              : selectedGem
                ? `Grant ${Math.max(1, Number.parseInt(gemQty, 10) || 1)}× ${selectedGem.label}`
                : "Select a gem"}
          </button>
        </section>
      )}

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Item</h2>
        <p className="text-xs text-gray-500">
          Type a filter and press + (or Enter) to add chips. Every chip must match — e.g.{" "}
          <span className="text-gray-400">80</span> then <span className="text-gray-400">archer</span> for Lv.80
          archer gear.
        </p>
        <AdminTokenSearchInput
          tokens={itemTokens}
          draft={itemDraft}
          onTokensChange={setItemTokens}
          onDraftChange={setItemDraft}
          placeholder="Search item templates…"
        />
        <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-kingdom-mid bg-kingdom-dark/80 p-1">
          {itemResults.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t)}
              className={clsx(
                "w-full text-left text-xs px-2 py-2 rounded-md hover:bg-kingdom-mid/60",
                selectedTemplate?.id === t.id
                  ? "bg-kingdom-mid/40 text-kingdom-glow"
                  : "text-gray-300",
              )}
            >
              <span className="font-semibold">{t.name}</span>
              <span className="text-gray-500">
                {" "}
                · {t.rarity} · Lv.{t.item_level} · {t.slot}
                {t.hero_class ? ` · ${t.hero_class}` : " · jewelry"}
              </span>
            </button>
          ))}
          {itemResults.length === 0 && (
            <p className="text-xs text-gray-500 px-2 py-3 text-center">No templates found</p>
          )}
        </div>
        <label className="block text-xs text-gray-400">
          Enhance level (optional)
          <input
            type="number"
            min={0}
            max={50}
            value={enhanceLevel}
            onChange={(e) => setEnhanceLevel(e.target.value)}
            className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
          />
        </label>
        <button
          type="button"
          disabled={loading !== null || !selectedTemplate}
          onClick={() =>
            run("item", () =>
              adminApi.grantItem(
                selectedTemplate!.id,
                targetUserId,
                Math.max(0, Number.parseInt(enhanceLevel, 10) || 0),
              ),
            )
          }
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "item"
            ? "Working…"
            : selectedTemplate
              ? `Grant ${selectedTemplate.name}`
              : "Select an item"}
        </button>
      </section>

      {luckStones.length > 0 && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <h2 className="font-bold text-white">Luck stones</h2>
          <p className="text-xs text-gray-500">
            Optional boost for blacksmith synthesis and gear enhance. Not dropped in catacombs — grant for testing.
          </p>
          <div className="flex flex-wrap gap-2">
            {luckStones.map((g) => (
              <button
                key={`luck-quick-${g.level}`}
                type="button"
                onClick={() => setSelectedGem(g)}
                className={clsx(
                  "text-xs px-3 py-1.5 rounded-full border",
                  selectedGem?.gem_type === "LUCK" && selectedGem.level === g.level
                    ? "border-violet-400 text-violet-200 bg-violet-950/50"
                    : "border-kingdom-mid text-gray-300 hover:border-violet-500/60",
                )}
              >
                {g.label}
              </button>
            ))}
          </div>
          <label className="block text-xs text-gray-400">
            Quantity
            <input
              type="number"
              min={1}
              value={gemQty}
              onChange={(e) => setGemQty(e.target.value)}
              className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
            />
          </label>
          <button
            type="button"
            disabled={loading !== null || !selectedGem || selectedGem.gem_type !== "LUCK"}
            onClick={() =>
              run("luck-stone", () =>
                adminApi.grantGem(
                  selectedGem!.gem_type,
                  selectedGem!.level,
                  parseAmount(gemQty),
                  targetUserId,
                ),
              )
            }
            className="w-full py-2.5 rounded-lg bg-violet-900/60 hover:bg-violet-800/60 border border-violet-600/50 text-white text-sm font-semibold disabled:opacity-50"
          >
            {loading === "luck-stone"
              ? "Working…"
              : selectedGem?.gem_type === "LUCK"
                ? `Grant ${Math.max(1, Number.parseInt(gemQty, 10) || 1)}× ${selectedGem.label}`
                : "Select a luck stone level above"}
          </button>
        </section>
      )}

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Gold</h2>
        <input
          type="number"
          min={1}
          max={1_000_000_000_000}
          value={goldAmount}
          onChange={(e) => setGoldAmount(e.target.value)}
          className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
        />
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              { label: "1M", value: 1_000_000 },
              { label: "10M", value: 10_000_000 },
              { label: "50M", value: 50_000_000 },
            ] as const
          ).map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => setGoldAmount(String(preset.value))}
              className="py-2 rounded-lg bg-kingdom-dark border border-kingdom-mid text-xs text-gray-300 hover:border-kingdom-glow"
            >
              {preset.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={loading !== null}
          onClick={() =>
            run("gold", () => adminApi.grantGoldTo(parseAmount(goldAmount), targetUserId))
          }
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "gold" ? "Working…" : `Grant ${Number(goldAmount || 0).toLocaleString()} gold`}
        </button>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Diamonds</h2>
        <input
          type="number"
          min={1}
          value={diamondAmount}
          onChange={(e) => setDiamondAmount(e.target.value)}
          className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
        />
        <button
          type="button"
          disabled={loading !== null}
          onClick={() =>
            run("diamonds", () =>
              adminApi.grantDiamondsTo(parseAmount(diamondAmount), targetUserId),
            )
          }
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "diamonds"
            ? "Working…"
            : `Grant ${Number(diamondAmount || 0).toLocaleString()} diamonds`}
        </button>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Star dust</h2>
        <input
          type="number"
          min={1}
          value={starDustAmount}
          onChange={(e) => setStarDustAmount(e.target.value)}
          className="w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
        />
        <button
          type="button"
          disabled={loading !== null}
          onClick={() =>
            run("star-dust", () =>
              adminApi.grantStarDustTo(parseAmount(starDustAmount), targetUserId),
            )
          }
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "star-dust"
            ? "Working…"
            : `Grant ${Number(starDustAmount || 0).toLocaleString()} star dust`}
        </button>
      </section>

      {craftMaterials.length > 0 && (
        <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
          <h2 className="font-bold text-white">Blacksmith / craft materials</h2>
          <p className="text-xs text-gray-500">
            Crypto for epic craft, MP dungeon shards, and legendary stones (Lv.40–80).
          </p>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {craftMaterials.map((m) => (
              <button
                key={`${m.kind}-${m.level ?? "flat"}`}
                type="button"
                onClick={() => setSelectedMaterial(m)}
                className={clsx(
                  "w-full text-left text-xs px-2 py-2 rounded-md border",
                  selectedMaterial?.kind === m.kind && selectedMaterial?.level === m.level
                    ? "border-kingdom-glow bg-kingdom-mid/40 text-kingdom-glow"
                    : "border-kingdom-mid text-gray-300 hover:bg-kingdom-mid/30",
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
          <label className="block text-xs text-gray-400">
            Amount
            <input
              type="number"
              min={1}
              value={materialQty}
              onChange={(e) => setMaterialQty(e.target.value)}
              className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
            />
          </label>
          <button
            type="button"
            disabled={loading !== null || !selectedMaterial}
            onClick={() =>
              run("craft-material", () =>
                adminApi.grantCraftMaterial(
                  selectedMaterial!.kind,
                  parseAmount(materialQty),
                  selectedMaterial!.level,
                  targetUserId,
                ),
              )
            }
            className="w-full py-2.5 rounded-lg bg-cyan-900/50 hover:bg-cyan-800/50 border border-cyan-600/40 text-white text-sm font-semibold disabled:opacity-50"
          >
            {loading === "craft-material"
              ? "Working…"
              : selectedMaterial
                ? `Grant ${Math.max(1, Number.parseInt(materialQty, 10) || 1).toLocaleString()}× ${selectedMaterial.label}`
                : "Select a material"}
          </button>
        </section>
      )}

      <section className="bg-kingdom-purple border border-rose-700/50 rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-rose-200">Reset character</h2>
        <p className="text-xs text-gray-400">
          Same as SQLAdmin «Reset Hero» for {targetLabel}: level 1, starter economy, both skill branches,
          equipment, consumables, gems, astrals, campaign &amp; dungeon runs, catacombs &amp; mine progress.
          Battle history is kept. Cannot be undone.
        </p>
        <button
          type="button"
          disabled={loading !== null}
          onClick={() => {
            const ok = window.confirm(
              `Reset hero for ${targetLabel}?\n\nThis wipes progression and inventory and applies the starter pack. Battle history stays. This cannot be undone.`,
            );
            if (!ok) return;
            void run("hero-reset", async () => {
              const res = await adminApi.resetHero(targetUserId);
              if (res.hero) {
                useGameStore.setState({ hero: res.hero });
              } else if (target.kind === "self") {
                await refreshHero();
              }
              if (res.balance) {
                useGameStore.setState({ balance: res.balance });
              } else if (target.kind === "self") {
                await refreshBalance();
              }
              return { message: res.message, balance: res.balance, hero: res.hero };
            });
          }}
          className="w-full py-2.5 rounded-lg bg-rose-900/60 hover:bg-rose-800/60 border border-rose-600/50 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "hero-reset" ? "Resetting…" : "Reset hero to starter state"}
        </button>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Catacombs</h2>
        <p className="text-xs text-gray-400">
          Reset Abandoned Catacombs progress for the selected hero (highest floor → 0, clear auto-clear
          timer).
        </p>
        <button
          type="button"
          disabled={loading !== null}
          onClick={() => run("catacombs-reset", () => adminApi.resetCatacombs(targetUserId))}
          className="w-full py-2.5 rounded-lg bg-rose-900/50 hover:bg-rose-800/50 border border-rose-600/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "catacombs-reset" ? "Working…" : "Reset catacombs progress"}
        </button>
      </section>

      {message && (
        <div className="text-xs text-emerald-300 bg-emerald-950/40 border border-emerald-800 rounded-lg p-3">
          {message}
        </div>
      )}
      {error && (
        <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded-lg p-3">
          {error}
        </div>
      )}
    </div>
  );
}
